import { getFacebookCookies, getOptimizelyData } from './general';
import { goToSelfSchedule, goToSelfScheduleOnPage } from './selfSchedule';

// Same-origin sessionStorage handoff contract with the self-schedule app
// (WEBDEV-4043). The payload shape here is the one corporate's
// selfScheduleHandoff.ts writes and the app's reader
// (corporate-monorepo apps/self-schedule/utils/onpage-handoff.ts) accepts:
// it checks `v`, rejects a `ts` older than 10 minutes, validates `clientInfo`
// with its own validators, ignores `attribution`, and deletes the key on read.
//
// Keep this byte-compatible with corporate and Smooth. Bumping HANDOFF_VERSION
// requires a coordinated change in the app, or every handoff is silently
// discarded and the visitor lands on a plain Step 1 with no error anywhere.
const HANDOFF_STORAGE_KEY = 'milan.ssa.handoff.v1';
const HANDOFF_VERSION = 1;

export const INTENT = {
    schedule: 'schedule',
    question: 'question',
};

// The app's own normalizePhone() requires exactly 10 digits and rejects an
// 11-digit "+1"-prefixed value — but theme-2023's PhoneInput mask
// (`+1\ 999-999-9999`) always bakes the country code in, so a fully-typed value
// arrives here with 11. Strip a leading US country code and format to the app's
// own display mask, "(XXX) XXX-XXXX", so a Back/Edit inside the app shows the
// same formatting the visitor typed.
//
// Returns '' when the input does not resolve to a US 10-digit number. That is
// deliberate: the app's isValidPhone then rejects the whole payload and drops
// the visitor onto Step 1 rather than skipping ahead with an unreachable lead.
function normalizePhoneForHandoff(rawPhone) {
    let digits = (rawPhone || '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
    }
    if (digits.length !== 10) return '';
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// Maps the form's Formik field names to the app's ClientInfo shape. smsOptIn is
// always true: the form has no checkbox, and clicking either CTA is the opt-in
// (WEBDEV-4494). The app only opts a visitor out on an explicit false.
export function buildOnPageClientInfo({ first_name, last_name, email, phone }) {
    return {
        firstName: first_name,
        lastName: last_name,
        email,
        phone: normalizePhoneForHandoff(phone),
        smsOptIn: true,
    };
}

// Reads whatever cloudamp persisted on this or an earlier page view. Scoped
// separately from the sessionStorage write below: localStorage can throw
// independently of sessionStorage (some Safari/ITP and embedded-webview configs
// block one but not the other), and a failure here is only a missing
// attribution field, not a reason to discard an otherwise-deliverable handoff.
function readStoredCloudampData() {
    try {
        return localStorage.getItem('cloudamp__data__c') || '';
    } catch {
        return '';
    }
}

/**
 * Writes the handoff payload and navigates to the self-schedule app.
 *
 * No lead is created on this side — the app creates it after the handoff.
 *
 * `intent` defaults to 'schedule' if falsy: defensive against an Enter-key
 * submit or any other path that reaches here without a button's onClick having
 * set it, since an empty intent would make the app fall back to a plain Step 1.
 *
 * `attribution` is sent best-effort. The app does not read it today — it does
 * its own same-origin reads instead (decision D3 in the WEBDEV-4043 plan) — but
 * it is included so the contract holds if that ever changes.
 *
 * sessionStorage can throw (Safari private browsing, blocked storage, quota).
 * If it does, the handoff is lost, but the visitor should still end up in the
 * app rather than stuck on a dead button: a normal Step 1 where they re-enter
 * their info is a better failure than nothing happening.
 */
export function writeOnPageHandoff(clientInfo, intent, { cloudampData, startedAt } = {}) {
    if (typeof window === 'undefined') return;

    try {
        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();

        const payload = {
            v: HANDOFF_VERSION,
            intent: intent || INTENT.schedule,
            clientInfo,
            attribution: {
                cloudamp__data__c: cloudampData || readStoredCloudampData(),
                fbp,
                fbc,
                experiment_exposures,
                experiment_user_ID,
            },
            // When the submit began, for the app's self_schedule_timing span.
            // Kept separate from `ts` (written at the end of the submit) since
            // the gap between the two is real latency worth preserving.
            startedAt: startedAt ?? null,
            ts: Date.now(),
        };

        sessionStorage.setItem(HANDOFF_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
        console.warn('[selfScheduleHandoff] Storage write failed — falling back to a normal Step 1 start', error);
        goToSelfSchedule();
        return;
    }

    goToSelfScheduleOnPage();
}

/**
 * Drops any payload left in sessionStorage on this origin.
 *
 * The app's reader deletes the key on every read, but that is a guarantee in
 * another repo. When the reader is not deployed, or the visitor presses Back
 * before it runs, a full contact record (name, email, phone) would otherwise
 * sit in sessionStorage for the life of the tab.
 *
 * Called on mount by the on-page form. That covers a visitor who returns to a
 * variant page — by Back, or by landing on one again. It does NOT cover a
 * visitor who reaches /schedule and never comes back: for them the payload
 * lives until the reader deletes it or the tab closes. This reduces the
 * dependency on the far end; it does not remove it.
 */
export function clearOnPageHandoff() {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.removeItem(HANDOFF_STORAGE_KEY);
    } catch {
        // Storage unavailable — nothing was written either, so nothing to clear.
    }
}
